import React, { memo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useOverlay } from '@fs/zion-ui'
import { useQueryParams, useHistory } from '@fs/zion-router'
import { useTranslation } from 'react-i18next'
import { AdminOnboardingOverview } from '@fs/zion-tree-groups'
import { useUser } from '@fs/zion-user'
import utils from '../../helpers/utils'
import getGroup from '../../services/getGroup'
import getGroupsList from '../../services/getGroupsList'
import getInviteInformation from '../../services/getInviteInformation'

import MaxGroupJoinErrorOverlay from './MaxGroupJoinErrorOverlay'
import JoinGroupErrorDialog from './JoinGroupErrorDialog'
import { useGroupLimits } from '../../providers/GroupLimitsProvider'
import JoinGroupDefaultOverlay from './JoinGroupDefaultOverlay'

function JoinGroupOverlay({ onJoinGroup }) {
  const queryParams = useQueryParams({ replaceState: true })
  const [t] = useTranslation()
  const overlay = useOverlay()
  const appHistory = useHistory()
  const [redirectUrl] = useState(queryParams.query.redirect_url)
  const [errorMessage, setErrorMessage] = useState('')
  const [currentGroups, setCurrentGroups] = useState([])
  const [groupDetails, setGroupDetails] = useState()
  const [groupId, setGroupId] = useState()
  const [inviteId, setInviteId] = useState()
  const { maxGroupsPerUser } = useGroupLimits()
  const [inviteSingleUse, setInviteSingleUse] = useState(false)
  const [showOnboardingOverlay, setShowOnboardingOverlay] = useState()
  const { cisId } = useUser()

  const page =
    currentGroups.length >= maxGroupsPerUser ? utils.overlayJoinPages.MAX_GROUPS : utils.overlayJoinPages.JOIN_GROUP

  const isResearcherGroup = groupDetails?.types.includes('RESEARCHER_TREE')

  const dialogTitle = isResearcherGroup
    ? 'group-management.join-research-tree.heading'
    : 'group-management.join-group.heading'

  // If `groupId` and `inviteId` is present on initial page load, start invitation flow
  useEffect(() => {
    const { groupId: groupIdParam, inviteId: inviteIdParam } = queryParams.query
    if (groupIdParam) setGroupId(groupIdParam)
    if (inviteIdParam) setInviteId(inviteIdParam)
    queryParams.removeQuery(groupIdParam)
    queryParams.removeQuery(inviteIdParam)

    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want this running on the component's first render
  }, [])

  // Start invitation flow if groupId and inviteId were both set
  const openOverlay = overlay.open // Reassign this for useEffect dep array purposes
  useEffect(() => {
    let isCurrent = true
    if (groupId && inviteId) {
      Promise.allSettled([getGroup(groupId), getGroupsList(), getInviteInformation(groupId, inviteId)])
        .then(([groupInfo, groupList, inviteInfo]) => {
          if (isCurrent) {
            const validInvite =
              !inviteInfo?.reason?.response?.headers?.warning?.includes('invite not found') &&
              groupInfo.status !== 'rejected' &&
              inviteInfo.status !== 'rejected'

            if (!validInvite) {
              setErrorMessage(t('group-management.invalid-invite.description'))
              openOverlay()
            } else {
              setCurrentGroups(groupList.value)
              setInviteSingleUse(inviteInfo?.value?.singleUse)
              // If user is already a member of the group, then reroute to the group's page
              const userIsAlreadyInGroup = groupList.value.some((group) => group.id === groupId)

              if (!userIsAlreadyInGroup) {
                setGroupDetails(groupInfo.value ?? {})
                openOverlay()
              }
            }
          }
          return null
        })
        .catch(console.error)
    }

    return () => {
      isCurrent = false
    }
  }, [groupId, inviteId, openOverlay, t])

  // Reset error message when user closes overlay
  useEffect(() => {
    if (!overlay.isOpen && !overlay.transitioning) {
      setErrorMessage('')
    }
  }, [overlay.transitioning, overlay.isOpen])

  if (maxGroupsPerUser === undefined) return null

  if (errorMessage) return <JoinGroupErrorDialog errorMessage={errorMessage} overlay={overlay} />

  if (!groupDetails || !groupId || !inviteId) return null
  // If user has reached max groups count
  if (page === utils.overlayJoinPages.MAX_GROUPS)
    return <MaxGroupJoinErrorOverlay overlay={overlay} maxGroupsPerUser={maxGroupsPerUser} />

  // User has not reached max groups count
  return (
    <>
      {(overlay.transitioning || overlay.isOpen) && (
        <JoinGroupDefaultOverlay
          onJoinGroup={onJoinGroup}
          groupDetails={groupDetails}
          overlay={overlay}
          inviteId={inviteId}
          setErrorMessage={setErrorMessage}
          maxGroupsPerUser={maxGroupsPerUser}
          appHistory={appHistory}
          inviteSingleUse={inviteSingleUse}
          cisId={cisId}
          redirectUrl={redirectUrl}
          setShowOnboardingOverlay={setShowOnboardingOverlay}
          dialogTitle={dialogTitle}
        />
      )}

      {showOnboardingOverlay && <AdminOnboardingOverview />}
    </>
  )
}
JoinGroupOverlay.propTypes = {
  /** A callback function for when the user joins a group. */
  onJoinGroup: PropTypes.func,
}

export default memo(JoinGroupOverlay)
